import { useEffect, useRef } from 'react'

import { IconBolt } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'

import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from '@/components/common/responsive-dialog'
import { PlanIconHeader, UpsellDialogShell } from '@/components/common/upsell'
import { Button } from '@/components/sui/button'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { useContextStore } from '@/stores/context'

import {
  trackContextualUpsellClosed,
  trackContextualUpsellConverted,
  trackContextualUpsellOpened,
} from './contextual-upsell-dialog.tracking'
import locales from './locales'

interface ContextualUpsellDialogProps {
  upsell: string
  open: boolean
  onOpenChange: (open: boolean) => void
  source?: string | null
}

interface ContextualUpsellDialogContentProps extends ContextualUpsellDialogProps {
  upsellTitle: string
  upsellDescription: string
  benefits: Array<string>
  t: (key: string, options?: Record<string, unknown>) => string
  onUpgradeClick?: () => void
}

/**
 * ContextualUpsellDialogManaged - For users managed by an administrator
 * Shows: upsell info + contact admin message + close button
 * Hides: benefits + upgrade button
 */
function ContextualUpsellDialogManaged({
  upsellTitle,
  upsellDescription,
  t,
}: ContextualUpsellDialogContentProps) {
  return (
    <UpsellDialogShell
      icon={<PlanIconHeader plan={null} icon={IconBolt} />}
      title={upsellTitle}
      description={upsellDescription}
      contactAdmin={{
        title: t('contactAdmin'),
        description: t('contactAdminDescription'),
      }}
    />
  )
}

/**
 * ContextualUpsellDialogAdmin - For administrators who can upgrade
 * Shows: upsell info + benefits + upgrade button
 */
function ContextualUpsellDialogAdmin({
  upsellTitle,
  upsellDescription,
  benefits,
  onOpenChange,
  onUpgradeClick,
  t,
  upsell,
}: ContextualUpsellDialogContentProps) {
  const navigate = useNavigate()

  const handleUpgrade = () => {
    onUpgradeClick?.()

    trackContextualUpsellConverted({
      upsell,
      currentPlan:
        useContextStore.getState().userAccountContext?.balanceInformation
          .featureType ?? 'Free',
      userRole: 'Administrator',
    })

    onOpenChange(false)
    navigate({ to: '/settings/billing/plans' })
  }

  return (
    <UpsellDialogShell
      icon={<PlanIconHeader plan={null} icon={IconBolt} />}
      title={upsellTitle}
      description={upsellDescription}
      benefits={{ title: t('benefitsTitle'), items: benefits }}
      actions={
        <Button
          onClick={handleUpgrade}
          className="cta-shimmer relative gap-2 overflow-hidden font-semibold"
          size="lg"
        >
          <IconBolt className="h-4 w-4" />
          {t('viewPlans')}
        </Button>
      }
    />
  )
}

/**
 * ContextualUpsellDialogUser - For non-administrator users
 * Shows: upsell info + benefits + contact admin message + close button
 * Hides: upgrade button
 */
function ContextualUpsellDialogUser({
  upsellTitle,
  upsellDescription,
  benefits,
  t,
}: ContextualUpsellDialogContentProps) {
  return (
    <UpsellDialogShell
      icon={<PlanIconHeader plan={null} icon={IconBolt} />}
      title={upsellTitle}
      description={upsellDescription}
      benefits={{ title: t('benefitsTitle'), items: benefits }}
      contactAdmin={{
        title: t('contactAdmin'),
        description: t('contactAdminDescription'),
      }}
    />
  )
}

export function ContextualUpsellDialog({
  upsell,
  open,
  onOpenChange,
  source,
}: ContextualUpsellDialogProps) {
  const { t } = useComponentTranslation(locales)
  const hasTrackedOpen = useRef(false)
  const hasConverted = useRef(false)
  const openedAtRef = useRef<number | null>(null)

  const isManagedBy = useContextStore(
    (state) => state.userAccountContext?.accountInformation.isManaged,
  )

  const isAdministrator = useContextStore(
    (state) =>
      state.userAccountContext?.userAccountInformation.role === 'Administrator',
  )

  const currentPlan = useContextStore(
    (state) =>
      state.userAccountContext?.balanceInformation.featureType ?? 'Free',
  )

  const userRole = isAdministrator ? 'Administrator' : 'User'

  const upsellTitle = t(`upsells.${upsell}.title`)
  const upsellDescription = t(`upsells.${upsell}.description`)
  const benefits = t(`upsells.${upsell}.benefits`, {
    returnObjects: true,
  }) as Array<string>

  useEffect(() => {
    if (open && !hasTrackedOpen.current) {
      trackContextualUpsellOpened({
        upsell,
        source: source ?? null,
        userRole,
        isManaged: isManagedBy ?? false,
        currentPlan,
      })
      hasTrackedOpen.current = true
      openedAtRef.current = Date.now()
    }

    if (!open) {
      hasTrackedOpen.current = false
    }
  }, [open, upsell, currentPlan, userRole, isManagedBy, source])

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && open) {
      const durationMs = openedAtRef.current
        ? Date.now() - openedAtRef.current
        : 0

      trackContextualUpsellClosed({
        upsell,
        converted: hasConverted.current,
        durationMs,
      })

      hasConverted.current = false
      openedAtRef.current = null
    }
    onOpenChange(newOpen)
  }

  const handleUpgradeClick = () => {
    hasConverted.current = true
  }

  const contentProps: ContextualUpsellDialogContentProps = {
    upsell,
    open,
    onOpenChange: handleOpenChange,
    upsellTitle,
    upsellDescription,
    benefits,
    t,
    source,
  }

  const renderContent = () => {
    if (isManagedBy) {
      return <ContextualUpsellDialogManaged {...contentProps} />
    }

    if (isAdministrator) {
      return (
        <ContextualUpsellDialogAdmin
          {...contentProps}
          onUpgradeClick={handleUpgradeClick}
        />
      )
    }

    return <ContextualUpsellDialogUser {...contentProps} />
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <ResponsiveDialogContent size="xl">
        {renderContent()}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
